import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ErrorView, PageTitle, Subnav, Can } from "@components/shared";
import {
  AssessmentInstrumentForm,
  AssessmentInstrumentStructure,
  AssessmentInstrumentChecklist,
} from "@components/AssessmentInstrument";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";
import { PERMISSIONS } from "@util/permissions";

function statusColor(status) {
  if (status === 1) return "green";
  if (status === 2) return "gray";
  if (status === 3) return "red";
  return "gray";
}

export default function AssessmentInstrumentDetail() {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [instrument, setInstrument] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const params = useParams();
  const navigate = useNavigate();
  const instrumentId = params.id;

  useEffect(() => {
    fetchData();
  }, [instrumentId]);

  function fetchData() {
    axios
      .get(`/assessment-instruments/${instrumentId}/`)
      .then(({ data }) => {
        setInstrument(data.response[0]);
        setError(null);
      })
      .catch((err) => {
        setError(err);
        setInstrument(null);
      });
  }

  function lockInstrument() {
    setBusyKey("lock");
    axios
      .post(`/assessment-instruments/${instrumentId}/lock/`)
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function unlockInstrument() {
    setBusyKey("unlock");
    axios
      .post(`/assessment-instruments/${instrumentId}/unlock/`)
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function toggleActive() {
    setBusyKey("active");
    axios
      .post(`/assessment-instruments/${instrumentId}/active-toggle/`)
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function deleteInstrument() {
    setBusyKey("delete");
    axios
      .delete(`/assessment-instruments/${instrumentId}/`)
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        navigate("/instruments");
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!instrument) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Group mb="lg">
        <PageTitle
          title={instrument.name}
          subtitle={t("assessment_instrument_singular")}
          icon={entityIcon.survey()}
          iconColor={entityColor.role}
        />
        <Group gap="xs">
          {instrument.locked && (
            <Badge variant="filled" color="orange">
              Locked
            </Badge>
          )}
          <Badge variant="light" color={statusColor(instrument.status)}>
            {instrument.status_formatted || t("status")}
          </Badge>
          {instrument.variety_formatted && (
            <Badge variant="light" color="blue">
              {instrument.variety_formatted}
            </Badge>
          )}
        </Group>
      </Group>
      <Box mt="md" mb="md">
        <Subnav
          links={[
            { text: t("detail"), to: `/instruments/${instrumentId}` },
            {
              text: "Structure",
              to: `/instruments/${instrumentId}/structure`,
            },
            {
              text: "Checklist",
              to: `/instruments/${instrumentId}/checklist`,
            },
          ]}
        />
      </Box>
      <Routes>
        <Route
          path="/"
          element={
            <Stack gap="sm">
              <Can
                permission={PERMISSIONS.ASSESSMENT_INSTRUMENTS_EDIT}
                fallback={
                  <Card>
                    <Stack gap="xs">
                      <Text>
                        <Text span fw={700}>
                          {t("name")}:{" "}
                        </Text>
                        {instrument.name}
                      </Text>
                      <Text>
                        <Text span fw={700}>
                          Instruction text:{" "}
                        </Text>
                        {instrument.instruction_text || "—"}
                      </Text>
                    </Stack>
                  </Card>
                }
              >
                <Card>
                  <AssessmentInstrumentForm
                    id={instrumentId}
                    name={instrument.name}
                    instructionText={instrument.instruction_text}
                    onSuccess={fetchData}
                  />
                </Card>
              </Can>

              <Can permission={PERMISSIONS.ASSESSMENT_INSTRUMENTS_EDIT}>
                <Card>
                  <Group gap="sm">
                    {instrument.locked ? (
                      <Button
                        variant="light"
                        loading={busyKey === "unlock"}
                        onClick={unlockInstrument}
                      >
                        Unlock
                      </Button>
                    ) : (
                      <Button
                        variant="light"
                        color="orange"
                        loading={busyKey === "lock"}
                        onClick={lockInstrument}
                      >
                        Lock
                      </Button>
                    )}
                    <Button
                      variant="light"
                      loading={busyKey === "active"}
                      onClick={toggleActive}
                    >
                      {t("activation_status")}
                    </Button>
                  </Group>
                </Card>
              </Can>

              <Can permission={PERMISSIONS.ASSESSMENT_INSTRUMENTS_DELETE}>
                <Card>
                  <Text size="sm" c="dimmed" mb="sm">
                    Soft-delete this assessment instrument.
                  </Text>
                  <Button
                    color="red"
                    variant="light"
                    loading={busyKey === "delete"}
                    onClick={deleteInstrument}
                  >
                    {t("delete")}
                  </Button>
                </Card>
              </Can>
            </Stack>
          }
        />
        <Route
          path="/structure"
          element={
            <AssessmentInstrumentStructure
              instrumentId={instrumentId}
              instrument={instrument}
              locked={!!instrument.locked}
              onChanged={(next) => {
                if (next) setInstrument(next);
                else fetchData();
              }}
            />
          }
        />
        <Route
          path="/checklist"
          element={
            <AssessmentInstrumentChecklist instrumentId={instrumentId} />
          }
        />
      </Routes>
    </div>
  );
}
